<script lang="ts" setup>
  import { cva } from 'class-variance-authority'
  import type { IRadioItem } from '@/components/ui/radio/types'

  const props = withDefaults(
    defineProps<{
      item: IRadioItem
      selected?: boolean
      idPrefix?: string
      idSuffix?: string | number
    }>(),
    {
      selected: false,
      idPrefix: 'radio',
      idSuffix: ''
    }
  )

  const inputId = computed(() => `${props.idPrefix}-${props.item.value}-${props.idSuffix}`)

  const itemBase = computed(() =>
    cva('flex h-6 w-6 items-center justify-center rounded-full border bg-white', {
      variants: {
        inactive: {
          true: 'bg-grey border-none',
          false: ''
        },
        locked: {
          true: 'border-border bg-white',
          false: ''
        },
        selected: {
          true: 'border-bt-blue-ui',
          false: ''
        },
        hover: {
          true: 'md:hover:border-bt-blue-ui',
          false: ''
        }
      }
    })({
      inactive: !props.selected && props.item.disabled,
      locked: props.selected && props.item.disabled,
      selected: props.selected && !props.item.disabled,
      hover: !props.selected && !props.item.disabled
    })
  )

  const indicatorBase = computed(() =>
    cva('flex h-4 w-4 items-center justify-center rounded-full bg-bt-blue-ui', {
      variants: {
        locked: {
          true: 'bg-border',
          false: 'bg-bt-blue-ui'
        }
      }
    })({
      locked: props.selected && props.item.disabled
    })
  )

  const labelBase = computed(() =>
    cva('flex flex-row items-center gap-4', {
      variants: {
        disabled: {
          true: 'cursor-default',
          false: 'cursor-pointer'
        }
      }
    })({
      disabled: props.item.disabled
    })
  )
</script>
<template>
  <label
    :for="inputId"
    :class="labelBase"
  >
    <RadioGroupItem
      :id="inputId"
      class="shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 group-[.has-error]:border group-[.has-error]:border-dark-error"
      :value="item.value"
      :disabled="item.disabled"
      :class="itemBase"
      data-test="radio"
    >
      <RadioGroupIndicator :class="indicatorBase" />
    </RadioGroupItem>
    <span
      v-if="item.label"
      class="text-body-1"
    >
      {{ item.label }}
    </span>
  </label>
</template>
